import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Paper,
  Box,
  Modal,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  SelectChangeEvent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  SingleFilter,
  RangeFilter,
  FilterArray,
  AutoCompleteField,
  AutoCompleteProfessions,
} from "../SingleFilter";
import { AgeRangeFilter } from "../SingleFilter/ageRangeFilter";
import { useSearchParams } from "react-router-dom";
import { useUser } from "../../hooks/useUser";
import { countActiveFilters, EXPERT_FILTER_KEYS, EXPERT_ALWAYS_COUNT_KEYS, isAdmin, isProTeam } from "../../utils";
import { useProfessions } from "../../hooks/useProfessions";
import axiosInstant from "../../api";

interface Props {
  onSearchUpdate: (params: any) => void;
  showBrandFilter?: boolean;
  showShortlistFilter?: boolean;
  shouldShowFilter?: boolean;
  /** Industry/Use-Case filter — only meaningful in a project-scoped pool
      (the backend classifies against the project's products). */
  showExpertTypeFilter?: boolean;
  type?: "users" | "experts";
  initialFilters?: any;
  onClearFilters?: () => void;
  searchParams?: URLSearchParams;
  setSearchParams?: any;
}

// Values match the API's expertType param / brand_projects_briefs.expert_type.
const expertTypeOptions = [
  { id: "industry", name: "Industry Expert" },
  { id: "use_case", name: "Use-Case Expert" },
];

export default function ListingFilters({
  onSearchUpdate,
  showBrandFilter,
  showShortlistFilter,
  shouldShowFilter,
  showExpertTypeFilter,
  type,
  initialFilters,
  onClearFilters,
  searchParams: searchParamsProp,
  setSearchParams: setSearchParamsProp,
}: Props) {
  const { role } = useUser();
  const [searchParamsLocal, setSearchParamsLocal] = useSearchParams();
  const searchParams = searchParamsProp || searchParamsLocal;
  const setSearchParams = setSearchParamsProp || setSearchParamsLocal;
  const [open, setOpen] = useState(false);
  const { professionsList, subProfessionLibrary } = useProfessions();
  const [brands, setBrands] = useState<Array<any>>([]);
  const [shortlists, setShortlists] = useState<Array<any>>([]);
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false);

  const activeFiltersCount = useMemo(
    () =>
      countActiveFilters(
        searchParams,
        [...EXPERT_FILTER_KEYS, ...FilterArray.map(filter => filter.filterKey)],
        { alwaysCountKeys: EXPERT_ALWAYS_COUNT_KEYS }
      ),
    [searchParams]
  );

  const queryParams = "experts=true";

  useEffect(() => {
    if (!hasInitializedFilters) {
      if (initialFilters && Object.keys(initialFilters).length > 0) {
        setSearchParams((prev: URLSearchParams) => {
          const updated = new URLSearchParams(prev.toString());
          Object.entries(initialFilters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && !updated.has(key)) {
              updated.set(key, value.toString());
            }
          });
          return updated;
        });
      }
      setHasInitializedFilters(true);
    }
  }, [setSearchParams, hasInitializedFilters]);

  useEffect(() => {
    const payload = Object.fromEntries(searchParams) as any;

    const ids = searchParams.get("ids");
    if (ids) payload.ids = ids.split(",");

    const ageRange = searchParams.get("agerange");
    if (ageRange) {
      payload.agerange = ageRange;
    }


    onSearchUpdate(payload);

  }, [searchParams]);

  useEffect(() => {
    if (showBrandFilter) {
      axiosInstant
        .get(`/brands?all=true&${queryParams}`)
        .then((res) => setBrands(res.data.rows))
        .catch(() => { });
    }
  }, [showBrandFilter]);

  useEffect(() => {
    if (showShortlistFilter) {
      axiosInstant
        .get("/shortlists?all=true")
        .then((res) => setShortlists(res.data.rows))
        .catch(() => { });
    }
  }, [showShortlistFilter]);

  const filterKeysToDelete = [
    ...EXPERT_FILTER_KEYS,
    ...FilterArray.map(filter => filter.filterKey),
  ];

  const clearFilters = () => {
    setSearchParams((prev: URLSearchParams) => {
      filterKeysToDelete.forEach(key => prev.delete(key));
      return prev;
    });
    onClearFilters?.();
  };

  const updateSearchParams = (
    key: string,
    event: SelectChangeEvent<string> | string
  ) => {

    let value: string;

    if (typeof event === "string") {
      value = event;
    } else {
      value = event.target.value;
    }

    setSearchParams((params: URLSearchParams) => {
      if (!value) params.delete(key);
      else params.set(key, value);
      params.delete("page");
      return params;
    });
  };

  const getCustomOptions = ({
    optionKey,
    optionMethod,
  }: {
    optionKey: string;
    optionMethod: Function;
  }) => {
    const valueArr = (searchParams.get(optionKey) || "").split(",");
    let result: any[] = [];
    valueArr.forEach((val) => {
      const opts = optionMethod(val);
      if (opts?.length) result.push(...opts);
    });
    return result;
  };

  if (!showBrandFilter && !shouldShowFilter) return null;

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{
          height: 40,
          borderRadius: "12px",
          width: "96px",
          border: "1px solid var(--border)",
          bgcolor: "var(--surface)",
          color: "var(--text-2)",
          display: "flex",
          alignItems: "center",
          textTransform: "none",
          px: "12px",
          minWidth: "fit-content",
          "& .MuiButton-startIcon": {
            marginRight: "8px",
          },
          "& > *": {
            marginRight: "8px",
          },
          "& > *:last-child": {
            marginRight: "0",
          },
        }}
        startIcon={<FilterListIcon />}
      >
        Filters
        {activeFiltersCount > 0 && (
          <span className="ml-2 text-[14px] font-[450] text-text-2">
            ({activeFiltersCount} Selected)
          </span>
        )}
      </Button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          component={Paper}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "calc(100% - 24px)", md: "90vw", lg: 1000 },
            maxWidth: 1000,
            maxHeight: { xs: "calc(100vh - 24px)", sm: "90vh" },
            bgcolor: "var(--surface)",
            borderRadius: "12px",
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
            padding: "2px",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ padding: { xs: "12px", sm: "16px" } }}
          >
            <Typography
              sx={{
                fontFamily: '"Circular Std", sans-serif',
                fontSize: { xs: "18px", sm: "24px" },
                fontWeight: 900,
              }}
            >
              Filters
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <hr style={{ margin: "0px 16px", color: "var(--border)" }} />

          {/* Filters Section */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: { xs: 1, sm: 1.5 },
              padding: { xs: "12px", sm: "16px" },
            }}
          >
            {type === "experts" && shouldShowFilter && (
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  borderBottom: "1px solid var(--border)",
                  paddingBottom: 1,
                  marginBottom: 0.5,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={searchParams?.get("hasFilmed") === "true"}
                      onChange={(e) =>
                        updateSearchParams("hasFilmed", e.target.checked ? "true" : "")
                      }
                      sx={{ color: "var(--muted)", "&.Mui-checked": { color: "var(--accent)" } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: 500,
                          fontFamily: "CircularStd",
                          color: "var(--text)",
                        }}
                      >
                        Has Filmed
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: "var(--text-2)", fontFamily: "CircularStd" }}
                      >
                        Only show experts with filmed videos on their projects
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "center", ml: 0 }}
                />
              </Box>
            )}

            {showBrandFilter && type === "experts" && (
              <SingleFilter
                options={brands.map((e) => e.name)}
                disabled={!brands.length}
                optionsObjArray={brands}
                updateSearch={updateSearchParams}
                filterKey="brand"
                title="Brands"
              />
            )}

            {showShortlistFilter && type === "experts" && (
              <SingleFilter
                options={shortlists.map((e) => e.name)}
                disabled={!shortlists.length}
                optionsObjArray={shortlists}
                updateSearch={updateSearchParams}
                filterKey="shortlist"
                title="Shortlist"
              />
            )}

            {showExpertTypeFilter && type === "experts" && shouldShowFilter && (
              <SingleFilter
                options={expertTypeOptions.map((e) => e.name)}
                optionsObjArray={expertTypeOptions}
                updateSearch={updateSearchParams}
                filterKey="expertType"
                title="Expert Type"
              />
            )}


            {type === "experts" &&
              shouldShowFilter &&
              FilterArray.map((filter) => {
                const {
                  title,
                  filterKey,
                  isAutoComplete,
                  isAutoCompleteKeyValue,
                  options,
                } = filter;

                if (filterKey === "status" && !isProTeam(role)) return null;

                let customOptions = options || [];
                if (filter.getOptions) {
                  customOptions = getCustomOptions(filter.getOptions);
                }

                let optionsObjArray: any[] = [];
                if (filterKey === "profession") {
                  optionsObjArray = professionsList.map((item) => ({
                    id: item.id,
                    label: item.name,
                  }));
                }

                if (filterKey === "subprofession") {
                  optionsObjArray = getCustomOptions({
                    optionKey: "profession",
                    optionMethod: (id: number) => {
                      const profession = professionsList.find(
                        (item) => item.id.toString() === id.toString()
                      );
                      if (profession) {
                        const subs = subProfessionLibrary[profession.id] || [];
                        return subs.map((item: any) => ({
                          id: item.id,
                          label: item.name,
                        }));
                      }
                      return [];
                    },
                  });
                }

                const isWide =
                  filterKey === "profession" || filterKey === "subprofession";

                return (
                  <Box
                    key={filterKey}
                    sx={{
                      gridColumn: isWide
                        ? { xs: "span 1", sm: "span 2", md: "span 3" }
                        : "span 1",
                    }}
                  >
                    {isAutoComplete && (
                      <AutoCompleteField
                        filterKey={filterKey}
                        options={customOptions}
                        label={title}
                        placeholder={`Select ${title} ...`}
                      />
                    )}
                    {isAutoCompleteKeyValue && (
                      <AutoCompleteProfessions
                        filterKey={filterKey}
                        optionsObjArray={optionsObjArray}
                        label={title}
                        placeholder={`Select ${title} ...`}
                      />
                    )}
                    {!isAutoComplete && !isAutoCompleteKeyValue && (
                      <SingleFilter
                        options={customOptions.filter(
                          (o): o is string => typeof o === "string"
                        )}
                        filterKey={filterKey}
                        title={title}
                        updateSearch={updateSearchParams}
                      />
                    )}
                  </Box>
                );
              })}

            {type === "experts" && shouldShowFilter && (
              <>
                <Box
                  sx={{
                    gridColumn: { xs: "span 1", sm: "span 2", md: "span 3" },
                    width: "100%",
                  }}
                >
                  <RangeFilter updateSearch={updateSearchParams} />
                </Box>
                <Box
                  sx={{
                    gridColumn: { xs: "span 1", sm: "span 2", md: "span 3" },
                    width: "100%",
                  }}
                >
                  <AgeRangeFilter updateSearch={updateSearchParams} />
                </Box>
              </>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "stretch", sm: "flex-end" },
              p: { xs: "12px", sm: "16px" },
            }}
          >
            <Button
              variant="outlined"
              onClick={clearFilters}
              sx={{
                width: { xs: "100%", sm: "auto" },
                textTransform: "capitalize",
                color: "var(--accent-text)",
                borderColor: "var(--accent-text)",
                "&:hover": { bgcolor: "var(--tint)" },
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
